
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState('en');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (hash: string) => {
    const element = document.querySelector(hash);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-32">
          {/* Logo - Larger */}
          <Link to="/" className="flex items-center cursor-pointer group">
            <img
              src="https://static.readdy.ai/image/8f4b99b6f50522f57c0c4089909ec764/cec408eade00b7f2bb8d035c2ad6c56f.png"
              alt="Rawafi Al Mutahidun"
              className="h-24 w-auto group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation - Larger */}
          <div className="hidden lg:flex items-center gap-10">
            <Link
              to="/"
              className={`font-inter font-bold text-lg transition-colors duration-300 cursor-pointer whitespace-nowrap hover:scale-105 transition-transform ${
                location.pathname === '/' ? 'text-accent' : 'text-primary hover:text-accent'
              }`}
            >
              Home
            </Link>
            
            <div className="relative group">
              <button className="font-inter font-bold text-lg text-primary hover:text-accent transition-colors duration-300 cursor-pointer whitespace-nowrap flex items-center gap-2 hover:scale-105 transition-transform">
                Cranes
                <i className="ri-arrow-down-s-line text-xl"></i>
              </button>
              <div className="absolute top-full left-0 mt-2 w-64 bg-white shadow-2xl rounded-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 overflow-hidden">
                <Link to="/cranes/heavy" className="block px-8 py-4 text-base font-inter font-semibold text-primary hover:bg-accent/10 hover:text-accent transition-colors cursor-pointer whitespace-nowrap">
                  Heavy Cranes (25-300 tons)
                </Link>
                <Link to="/cranes/light" className="block px-8 py-4 text-base font-inter font-semibold text-primary hover:bg-accent/10 hover:text-accent transition-colors cursor-pointer whitespace-nowrap">
                  Light Cranes (3-19 tons)
                </Link>
              </div>
            </div>

            <div className="relative group">
              <button className="font-inter font-bold text-lg text-primary hover:text-accent transition-colors duration-300 cursor-pointer whitespace-nowrap flex items-center gap-2 hover:scale-105 transition-transform">
                Equipment
                <i className="ri-arrow-down-s-line text-xl"></i>
              </button>
              <div className="absolute top-full left-0 mt-2 w-56 bg-white shadow-2xl rounded-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 overflow-hidden">
                <Link to="/equipment/flatbed" className="block px-8 py-4 text-base font-inter font-semibold text-primary hover:bg-accent/10 hover:text-accent transition-colors cursor-pointer whitespace-nowrap">
                  Flatbeds
                </Link>
                <Link to="/equipment/manlift" className="block px-8 py-4 text-base font-inter font-semibold text-primary hover:bg-accent/10 hover:text-accent transition-colors cursor-pointer whitespace-nowrap">
                  Manlift
                </Link>
                <Link to="/equipment/forklift" className="block px-8 py-4 text-base font-inter font-semibold text-primary hover:bg-accent/10 hover:text-accent transition-colors cursor-pointer whitespace-nowrap">
                  Forklift
                </Link>
                <Link to="/equipment/scissor-lift" className="block px-8 py-4 text-base font-inter font-semibold text-primary hover:bg-accent/10 hover:text-accent transition-colors cursor-pointer whitespace-nowrap">
                  Scissor Lift
                </Link>
              </div>
            </div>

            <Link
              to="/about"
              className={`font-inter font-bold text-lg transition-colors duration-300 cursor-pointer whitespace-nowrap hover:scale-105 transition-transform ${
                location.pathname === '/about' ? 'text-accent' : 'text-primary hover:text-accent'
              }`}
            >
              About Us
            </Link>

            <a
              href="/#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#contact');
              }}
              className="font-inter font-bold text-lg text-primary hover:text-accent transition-colors duration-300 cursor-pointer whitespace-nowrap hover:scale-105 transition-transform"
            >
              Contact
            </a>
          </div>

          {/* CTA & Language Switcher */}
          <div className="hidden lg:flex items-center gap-5">
            <button
              onClick={toggleLanguage}
              className="w-12 h-12 rounded-full border-2 border-neutral-200 flex items-center justify-center hover:border-accent hover:text-accent transition-all duration-300 cursor-pointer hover:scale-110"
              aria-label="Switch Language"
            >
              <i className="ri-global-line text-xl"></i>
            </button>
            
            <a
              href="/#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection('#contact');
              }}
              className="px-8 py-4 bg-accent text-white rounded-full font-inter font-bold text-base hover:bg-accent-dark transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 hover:shadow-xl"
            >
              Get Quote
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-12 h-12 flex items-center justify-center text-primary cursor-pointer"
            aria-label="Toggle Menu"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-3xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-6 border-t border-neutral-200">
            <div className="flex flex-col gap-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-inter font-medium text-base text-primary hover:text-accent transition-colors cursor-pointer"
              >
                Home
              </Link>
              
              <div className="font-inter font-semibold text-base text-primary">Cranes</div>
              <Link
                to="/cranes/heavy"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-inter text-base text-primary hover:text-accent transition-colors cursor-pointer pl-4"
              >
                Heavy Cranes (25-300 tons)
              </Link>
              <Link
                to="/cranes/light"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-inter text-base text-primary hover:text-accent transition-colors cursor-pointer pl-4"
              >
                Light Cranes (3-19 tons)
              </Link>

              <div className="font-inter font-semibold text-base text-primary">Equipment</div>
              <Link
                to="/equipment/flatbed"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-inter text-base text-primary hover:text-accent transition-colors cursor-pointer pl-4"
              >
                Flatbeds
              </Link>
              <Link
                to="/equipment/manlift"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-inter text-base text-primary hover:text-accent transition-colors cursor-pointer pl-4"
              >
                Manlift
              </Link>
              <Link
                to="/equipment/forklift"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-inter text-base text-primary hover:text-accent transition-colors cursor-pointer pl-4"
              >
                Forklift
              </Link>
              <Link
                to="/equipment/scissor-lift"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-inter text-base text-primary hover:text-accent transition-colors cursor-pointer pl-4"
              >
                Scissor Lift
              </Link>

              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className="font-inter font-medium text-base text-primary hover:text-accent transition-colors cursor-pointer"
              >
                About Us
              </Link>

              <a
                href="/#contact"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('#contact');
                }}
                className="font-inter font-medium text-base text-primary hover:text-accent transition-colors cursor-pointer"
              >
                Contact
              </a>

              <button
                onClick={toggleLanguage}
                className="px-6 py-3 border-2 border-neutral-200 text-primary rounded-full font-inter font-semibold text-base hover:border-accent hover:text-accent transition-all duration-300 cursor-pointer whitespace-nowrap text-center"
              >
                <i className="ri-global-line mr-2"></i>
                {language === 'en' ? 'العربية' : 'English'}
              </button>

              <a
                href="/#contact"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('#contact');
                }}
                className="px-6 py-3 bg-accent text-white rounded-full font-inter font-semibold text-base hover:bg-accent-dark transition-all duration-300 cursor-pointer whitespace-nowrap text-center"
              >
                Get Quote
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
